/**
 * Battle arena generation, independent of the pre-battle mini-game.
 *
 * The arena used to be derived from the terrain pieces a player placed. It is
 * now rolled from its own seed: the mini-game decides *which* Garaimon appears,
 * and nothing about the ground it appears on. The fingerprint below is prefixed
 * so a roll-era arena can always be told apart from a puzzle-era one in saved
 * episodes.
 *
 * The other job here is the guarantee that an arena is playable. Obstacle
 * positions are rolled, and rolling positions can seal the two creatures off
 * from each other, which would quietly turn a battle into an unavoidable loss.
 * A layout is therefore only accepted once a walking route between the two
 * spawn points has actually been found. If no layout can be found, this fails
 * loudly rather than shipping a sealed arena.
 */

import { seededRandom } from './game';
import { BattleNavigation, type BattleBounds, type BattlePoint, type BattleRect } from './battleNavigation';
import type { ArenaFeatures } from './types';

export type ObstacleKind = 'water' | 'structure' | 'tree' | 'rock';

export interface ObstacleFootprint {
  readonly width: number;
  readonly height: number;
}

export interface ArenaObstacle {
  readonly kind: ObstacleKind;
  readonly x: number;
  readonly y: number;
}

/** How far in from the arena edge an obstacle centre may sit, matching how the scene has always placed them. */
export const OBSTACLE_MARGIN_X = 42;
export const OBSTACLE_MARGIN_TOP = 70;
export const OBSTACLE_MARGIN_BOTTOM = 70;

/**
 * The radius of a battling creature's body.
 *
 * The arena layout is checked against this, and the battle scene navigates with
 * it, so both must be the same number. It lives here so there is one value
 * rather than two that can drift apart.
 */
export const BATTLE_BODY_RADIUS = 14;

/**
 * How many layout seeds to try before giving up.
 *
 * Measured rather than guessed. Over 18,000 rolls across six phone sizes, 71% of
 * arenas were playable on the first attempt and the 99th percentile needed four.
 * Over a further 120,000 rolls on the two smallest sizes - the tightest cases,
 * where a path is hardest to thread - the worst single arena needed fifteen.
 * This leaves three times that headroom, so a layout only fails when the arena is
 * genuinely broken rather than merely unlucky.
 */
const LAYOUT_ATTEMPTS = 48;

/** Raised when no layout with a walkable route could be found, so the failure is never silent. */
export class ArenaLayoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ArenaLayoutError';
  }
}

function pick<T>(random: () => number, values: readonly T[]): T {
  const value = values[Math.floor(random() * values.length)];
  if (value === undefined) throw new Error('Garaimon: an arena feature was chosen from an empty list.');
  return value;
}

/**
 * A fresh random stream for one layout attempt.
 *
 * Exported because the attempt count is part of the layout contract: a seed that
 * needed several attempts to become playable must reproduce the same way when
 * inspected, and the sweep harness checks that directly.
 */
export function arenaLayoutRandom(seed: number, attempt: number): () => number {
  return seededRandom((seed ^ Math.imul(attempt + 1, 0x9e3779b9)) >>> 0);
}

/**
 * Rolls the arena's character from its own seed.
 *
 * Each feature keeps the range it had when it was derived from the terrain
 * pieces, so arenas look and play as they always have, and the affinity passed
 * in comes from the encountered Garaimon rather than from any placement.
 */
export function rollArena(seed: number, affinity: string): ArenaFeatures {
  const random = seededRandom(seed ^ 0x51ed270b);
  // Weighted rather than uniform, so a typical arena is mostly open with a few
  // landmarks, and an obstacle-packed arena stays uncommon.
  const water = pick(random, [0, 0, 0, 1, 1, 2, 3]);
  const structure = pick(random, [0, 0, 0, 0, 1, 1, 2, 3]);
  const cover = pick(random, [1, 1, 2, 2, 2, 3, 3, 4, 5]);
  const roughGround = pick(random, [0, 1, 1, 1, 2, 2, 3, 4]);
  const openLanes = pick(random, [1, 2, 2, 3, 3, 3, 4, 4, 5]);
  return {
    cover,
    openLanes,
    roughGround,
    water,
    structure,
    affinity,
    seed,
    // Prefixed so a rolled arena is never confused with a terrain-placement one.
    fingerprint: `roll-${seed}-${cover}${openLanes}${roughGround}${water}${structure}`,
  };
}

/**
 * The kind of each obstacle, in placement order.
 *
 * Water comes first, then structures, then the cover, then loose rock. Keeping
 * the order fixed means the same arena always shows the same mix, and the total
 * is bounded so a crowded roll cannot carpet the floor.
 */
export function arenaObstacleKinds(features: ArenaFeatures): ObstacleKind[] {
  const count = Math.max(3, Math.min(9, features.cover + features.roughGround + features.water + features.structure + 1));
  const kinds: ObstacleKind[] = [];
  for (let index = 0; index < count; index += 1) {
    if (index < features.water) kinds.push('water');
    else if (index < features.water + features.structure) kinds.push('structure');
    else if (index < features.water + features.structure + features.cover) kinds.push('tree');
    else kinds.push('rock');
  }
  return kinds;
}

/**
 * The collision rectangle an obstacle occupies.
 *
 * A sprite is drawn from its base, but only the footprint blocks movement, and
 * the scene centres that footprint on the placement point. The collision box is
 * therefore centred here too, in the worst-case size for its kind.
 */
export function obstacleRect(obstacle: ArenaObstacle, footprint: ObstacleFootprint): BattleRect {
  const left = obstacle.x - footprint.width / 2;
  const top = obstacle.y - footprint.height / 2;
  const right = obstacle.x + footprint.width / 2;
  const bottom = obstacle.y + footprint.height / 2;
  return {
    left,
    right,
    top,
    bottom,
    x: left,
    y: top,
    width: footprint.width,
    height: footprint.height,
    contains: (x: number, y: number) => x >= left && x <= right && y >= top && y <= bottom,
  };
}

/**
 * The footprint a guaranteed layout must allow for, per kind: the largest sprite
 * of that kind.
 *
 * The guarantee is made before any sprite is chosen, so it is made against the
 * worst case. A smaller sprite can only ever make the arena easier to cross.
 */
export function worstCaseFootprints(
  props: ReadonlyArray<{ kind: ObstacleKind; width: number; height: number; footprintWidth: number; footprintHeight: number }>,
): Record<ObstacleKind, ObstacleFootprint> {
  const footprints: Record<ObstacleKind, ObstacleFootprint> = {
    water: { width: 0, height: 0 },
    structure: { width: 0, height: 0 },
    tree: { width: 0, height: 0 },
    rock: { width: 0, height: 0 },
  };
  for (const prop of props) {
    const current = footprints[prop.kind];
    footprints[prop.kind] = {
      width: Math.max(current.width, prop.width * prop.footprintWidth),
      height: Math.max(current.height, prop.height * prop.footprintHeight),
    };
  }
  return footprints;
}

/**
 * Whether a body of the given radius could walk between the two points.
 *
 * This uses the battle's own navigation, so the check is the same one the
 * creatures use rather than a second opinion that might disagree with it.
 */
export function arenaHasRoute(
  obstacles: readonly ArenaObstacle[],
  bounds: BattleBounds,
  radius: number,
  from: BattlePoint,
  to: BattlePoint,
  footprints: Record<ObstacleKind, ObstacleFootprint>,
): boolean {
  const rects = obstacles.map((obstacle) => obstacleRect(obstacle, footprints[obstacle.kind]));
  const navigation = new BattleNavigation(rects, bounds, radius);
  if (!navigation.canStand(from.x, from.y) || !navigation.canStand(to.x, to.y)) return false;
  return navigation.nextWaypoint(from, to) !== undefined;
}

export interface ArenaPlacementRequest {
  readonly features: ArenaFeatures;
  readonly viewport: { readonly width: number; readonly height: number };
  readonly bounds: BattleBounds;
  readonly bodyRadius: number;
  /** The two positions the creatures start from. Obstacles are kept clear of them. */
  readonly spawns: readonly [BattlePoint, BattlePoint];
  readonly footprints: Record<ObstacleKind, ObstacleFootprint>;
}

/**
 * Places the arena's obstacles so that a route between the two spawn points
 * always exists.
 *
 * Positions are rolled from a layout seed derived from the arena seed, then
 * checked against the battle's own navigation. A layout that buries a spawn
 * point, or seals either creature off from the other, is discarded and rolled
 * again. Nothing is dropped or nudged to make a bad layout pass, because that
 * would quietly change the arena; the layout is simply rejected.
 *
 * The guarantee is made against the largest sprite of each kind, so it holds
 * whichever sprite is later drawn. If none of the attempts works this throws:
 * an unwinnable arena is a bug to fix, not a state to hide.
 */
export function placeArenaObstacles(request: ArenaPlacementRequest): ArenaObstacle[] {
  const { features, viewport, bounds, bodyRadius, spawns, footprints } = request;
  const kinds = arenaObstacleKinds(features);
  const spanX = viewport.width - OBSTACLE_MARGIN_X * 2;
  const spanY = viewport.height - OBSTACLE_MARGIN_TOP - OBSTACLE_MARGIN_BOTTOM;
  if (spanX <= 0 || spanY <= 0) {
    throw new ArenaLayoutError(
      `Garaimon: the arena is ${viewport.width}x${viewport.height}, too small to place obstacles in. ` +
        `It needs to be more than ${OBSTACLE_MARGIN_X * 2} wide and ${OBSTACLE_MARGIN_TOP + OBSTACLE_MARGIN_BOTTOM} tall.`,
    );
  }

  for (let attempt = 0; attempt < LAYOUT_ATTEMPTS; attempt += 1) {
    const random = arenaLayoutRandom(features.seed, attempt);
    const obstacles: ArenaObstacle[] = kinds.map((kind) => ({
      kind,
      x: OBSTACLE_MARGIN_X + random() * spanX,
      y: OBSTACLE_MARGIN_TOP + random() * spanY,
    }));
    if (arenaHasRoute(obstacles, bounds, bodyRadius, spawns[0], spawns[1], footprints)) return obstacles;
  }

  throw new ArenaLayoutError(
    `Garaimon: no layout for arena ${features.fingerprint} left a walkable route between ` +
      `${spawns[0].x.toFixed(0)},${spawns[0].y.toFixed(0)} and ${spawns[1].x.toFixed(0)},${spawns[1].y.toFixed(0)} after ${LAYOUT_ATTEMPTS} attempts ` +
      `in a ${viewport.width}x${viewport.height} arena. The arena is too cramped or the obstacles are too large; this needs fixing rather than working around.`,
  );
}
